import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
type ENumberEntry = {
  code: string;
  name: string;
  primaryUse: string;
  source: string;
  category: string;
};
const entries: ENumberEntry[] = [{
  code: 'E100',
  name: 'Curcumin',
  primaryUse: 'Natural orange-yellow colour used in sauces, soups, and confectionery.',
  source: 'Extracted from turmeric spice.',
  category: 'Colours'
}, {
  code: 'E101',
  name: 'Riboflavin',
  primaryUse: 'Yellow/orange colour used to fortify foods and colour processed cheese.',
  source: 'Also known as Vitamin B2.',
  category: 'Colours'
}, {
  code: 'E102',
  name: 'Tartrazine',
  primaryUse: 'Synthetic yellow dye widely used in soft drinks, sweets, and processed foods.',
  source: 'Part of the "Southampton Six" which may affect children’s activity/attention.',
  category: 'Colours'
}, {
  code: 'E110',
  name: 'Sunset Yellow FCF',
  primaryUse: 'Synthetic yellow/orange dye used in baked goods, snacks, and sweets.',
  source: 'Part of the "Southampton Six."',
  category: 'Colours'
}, {
  code: 'E120',
  name: 'Cochineal / Carmines',
  primaryUse: 'Natural red colour used widely in foods.',
  source: 'Obtained from crushed insects; not vegetarian/vegan friendly.',
  category: 'Colours'
}, {
  code: 'E133',
  name: 'Brilliant Blue FCF',
  primaryUse: 'Synthetic blue dye used in snacks and sometimes mixed with yellow to create green colours.',
  source: 'Synthetic food colour.',
  category: 'Colours'
}, {
  code: 'E140',
  name: 'Chlorophylls',
  primaryUse: 'Natural green pigment used to enhance the colour of green vegetables.',
  source: 'Extracted from plants.',
  category: 'Colours'
}, {
  code: 'E150a',
  name: 'Plain Caramel',
  primaryUse: 'Brown colour common in colas and brown bread.',
  source: 'Made from burning sugar.',
  category: 'Colours'
}, {
  code: 'E160a',
  name: 'Beta-Carotene',
  primaryUse: 'Natural colour used in margarine and cheese.',
  source: 'Derived from carrots.',
  category: 'Colours'
}, {
  code: 'E160b',
  name: 'Annatto',
  primaryUse: 'Natural colour used in cheese and butter.',
  source: 'Derived from seeds.',
  category: 'Colours'
}, {
  code: 'E162',
  name: 'Beetroot Red',
  primaryUse: 'Natural colour used in yogurt and desserts.',
  source: 'Derived from beetroot.',
  category: 'Colours'
}, {
  code: 'E163',
  name: 'Anthocyanins',
  primaryUse: 'Natural colour used in juice and candy.',
  source: 'Derived from berries and grapes.',
  category: 'Colours'
}, {
  code: 'E171',
  name: 'Titanium Dioxide',
  primaryUse: 'White colour whitener used in candies and frostings.',
  source: 'Banned as a food additive in the EU since 2022.',
  category: 'Colours'
}, {
  code: 'E200',
  name: 'Sorbic Acid',
  primaryUse: 'Preservative used in soft drinks, cheeses, and baked goods.',
  source: 'E202 (Potassium Sorbate) is its common salt form.',
  category: 'Preservatives'
}, {
  code: 'E202',
  name: 'Potassium Sorbate',
  primaryUse: 'Prevents mold in yogurt and other foods.',
  source: 'Common salt form of sorbic acid.',
  category: 'Preservatives'
}, {
  code: 'E210',
  name: 'Benzoic Acid',
  primaryUse: 'Preserves beverages.',
  source: 'Common in soft drinks.',
  category: 'Preservatives'
}, {
  code: 'E211',
  name: 'Sodium Benzoate',
  primaryUse: 'Commonly used in acidic foods like soft drinks, sauces, and pickles.',
  source: 'Preservative.',
  category: 'Preservatives'
}, {
  code: 'E220',
  name: 'Sulfur Dioxide',
  primaryUse: 'Preservative, bleaching agent, and stabilizer found in dried fruits, wines, and sausages.',
  source: 'Gas used in food preservation.',
  category: 'Preservatives'
}, {
  code: 'E249',
  name: 'Potassium Nitrite',
  primaryUse: 'Cures meat.',
  source: 'Common in sausage.',
  category: 'Preservatives'
}, {
  code: 'E250',
  name: 'Sodium Nitrite',
  primaryUse: 'Key preservative for curing meats like hot dogs and bacon; also helps fix red colour.',
  source: 'Cured meat preservative.',
  category: 'Preservatives'
}, {
  code: 'E251',
  name: 'Sodium Nitrate',
  primaryUse: 'Preserves meat.',
  source: 'Common in deli meats.',
  category: 'Preservatives'
}, {
  code: 'E260',
  name: 'Acetic Acid',
  primaryUse: 'Preservative and acidity regulator.',
  source: 'Found naturally in vinegar.',
  category: 'Preservatives'
}, {
  code: 'E300',
  name: 'Ascorbic Acid',
  primaryUse: 'Prevents browning in fruit juices and fruit-based products.',
  source: 'Also known as Vitamin C.',
  category: 'Antioxidants & Acidity Regulators'
}, {
  code: 'E301',
  name: 'Sodium Ascorbate',
  primaryUse: 'Antioxidant.',
  source: 'Common in processed meats.',
  category: 'Antioxidants & Acidity Regulators'
}, {
  code: 'E306',
  name: 'Tocopherol',
  primaryUse: 'Added to fats and oils to prevent them from becoming rancid.',
  source: 'Rich in Vitamin E.',
  category: 'Antioxidants & Acidity Regulators'
}, {
  code: 'E322',
  name: 'Lecithins',
  primaryUse: 'Keeps ingredients like oil and water mixed.',
  source: 'Natural emulsifier often derived from soy or sunflower oil.',
  category: 'Antioxidants & Acidity Regulators'
}, {
  code: 'E325',
  name: 'Sodium Lactate',
  primaryUse: 'Acidity regulator.',
  source: 'Common in meat products.',
  category: 'Antioxidants & Acidity Regulators'
}, {
  code: 'E330',
  name: 'Citric Acid',
  primaryUse: 'Widely used acidity regulator in soft drinks, jams, and sauces.',
  source: 'Found naturally in citrus fruits.',
  category: 'Antioxidants & Acidity Regulators'
}, {
  code: 'E331',
  name: 'Sodium Citrate',
  primaryUse: 'Buffer.',
  source: 'Common in cheese and beverages.',
  category: 'Antioxidants & Acidity Regulators'
}, {
  code: 'E338',
  name: 'Phosphoric Acid',
  primaryUse: 'Gives cola drinks a sharp flavour and acts as an antioxidant.',
  source: 'Used primarily in cola drinks.',
  category: 'Antioxidants & Acidity Regulators'
}, {
  code: 'E406',
  name: 'Agar-Agar',
  primaryUse: 'Natural thickener commonly used in dairy products and desserts.',
  source: 'Derived from red seaweed.',
  category: 'Thickeners, Stabilizers & Emulsifiers'
}, {
  code: 'E407',
  name: 'Carrageenan',
  primaryUse: 'Thickener and stabilizer.',
  source: 'Derived from red seaweed; common in dairy products.',
  category: 'Thickeners, Stabilizers & Emulsifiers'
}, {
  code: 'E410',
  name: 'Locust Bean Gum',
  primaryUse: 'Thickener and stabilizer.',
  source: 'Derived from carob seeds; common in ice cream.',
  category: 'Thickeners, Stabilizers & Emulsifiers'
}, {
  code: 'E412',
  name: 'Guar Gum',
  primaryUse: 'Thickener.',
  source: 'Derived from guar beans; common in sauces and ice cream.',
  category: 'Thickeners, Stabilizers & Emulsifiers'
}, {
  code: 'E414',
  name: 'Acacia Gum',
  primaryUse: 'Stabilizer and thickener in confectionery and soft drinks.',
  source: 'Also known as Gum Arabic; derived from the acacia tree.',
  category: 'Thickeners, Stabilizers & Emulsifiers'
}, {
  code: 'E415',
  name: 'Xanthan Gum',
  primaryUse: 'Stabilizer.',
  source: 'Produced through fermentation; common in salad dressing.',
  category: 'Thickeners, Stabilizers & Emulsifiers'
}, {
  code: 'E440',
  name: 'Pectin',
  primaryUse: 'Natural gelling agent essential for making jams and jellies.',
  source: 'Found in apples and citrus fruits.',
  category: 'Thickeners, Stabilizers & Emulsifiers'
}, {
  code: 'E441',
  name: 'Gelatin',
  primaryUse: 'Gelling and thickening agent.',
  source: 'Animal collagen; not vegan.',
  category: 'Thickeners, Stabilizers & Emulsifiers'
}, {
  code: 'E460',
  name: 'Cellulose',
  primaryUse: 'Thickener, filler, and anti-caking agent in processed foods.',
  source: 'Derived from plant material.',
  category: 'Thickeners, Stabilizers & Emulsifiers'
}, {
  code: 'E471',
  name: 'Mono- & Diglycerides',
  primaryUse: 'Emulsifier.',
  source: 'Derived from vegetable oils; common in bread.',
  category: 'Thickeners, Stabilizers & Emulsifiers'
}, {
  code: 'E500',
  name: 'Sodium Carbonate',
  primaryUse: 'Baking and acidity regulation.',
  source: 'Includes baking soda.',
  category: 'pH Regulators & Anti-Caking Agents'
}, {
  code: 'E500(ii)',
  name: 'Sodium Bicarbonate',
  primaryUse: 'Baking soda.',
  source: 'Used as a raising agent.',
  category: 'pH Regulators & Anti-Caking Agents'
}, {
  code: 'E504',
  name: 'Magnesium Carbonate',
  primaryUse: 'Anti-caking agent.',
  source: 'Used in powdered foods.',
  category: 'pH Regulators & Anti-Caking Agents'
}, {
  code: 'E509',
  name: 'Calcium Chloride',
  primaryUse: 'Firmness agent.',
  source: 'Used to support texture.',
  category: 'pH Regulators & Anti-Caking Agents'
}, {
  code: 'E542',
  name: 'Edible Bone Phosphate',
  primaryUse: 'Food additive.',
  source: 'Derived from animal bones; not vegan.',
  category: 'pH Regulators & Anti-Caking Agents'
}, {
  code: 'E551',
  name: 'Silicon Dioxide',
  primaryUse: 'Anti-caking agent in powdered foods such as salt and spices.',
  source: 'Mineral-based anti-caking agent.',
  category: 'pH Regulators & Anti-Caking Agents'
}, {
  code: 'E620',
  name: 'Glutamic Acid',
  primaryUse: 'Flavour enhancer.',
  source: 'Common in soups.',
  category: 'Flavour Enhancers'
}, {
  code: 'E621',
  name: 'Monosodium Glutamate (MSG)',
  primaryUse: 'Enhances savoury or umami flavour in chips, fast food, and frozen meals.',
  source: 'Flavour enhancer.',
  category: 'Flavour Enhancers'
}, {
  code: 'E627',
  name: 'Disodium Guanylate',
  primaryUse: 'Flavour enhancer.',
  source: 'Common in snacks.',
  category: 'Flavour Enhancers'
}, {
  code: 'E631',
  name: 'Disodium Inosinate',
  primaryUse: 'Flavour enhancer.',
  source: 'Common in instant noodles.',
  category: 'Flavour Enhancers'
}, {
  code: 'E635',
  name: 'Disodium Ribonucleotides',
  primaryUse: 'Flavour enhancer.',
  source: 'Common in seasoning blends.',
  category: 'Flavour Enhancers'
}, {
  code: 'E901',
  name: 'Beeswax',
  primaryUse: 'Glazing agent.',
  source: 'Derived from bees; often not considered vegan.',
  category: 'Miscellaneous & New Additives'
}, {
  code: 'E903',
  name: 'Carnauba Wax',
  primaryUse: 'Fruit coating.',
  source: 'Derived from leaves of the Brazilian carnauba palm.',
  category: 'Miscellaneous & New Additives'
}, {
  code: 'E904',
  name: 'Shellac',
  primaryUse: 'Candy coating.',
  source: 'Derived from the lac insect; not vegan.',
  category: 'Miscellaneous & New Additives'
}, {
  code: 'E920',
  name: 'L-Cysteine',
  primaryUse: 'Food processing additive.',
  source: 'Can be synthetic or fermentation-derived; source can vary.',
  category: 'Miscellaneous & New Additives'
}, {
  code: 'E950',
  name: 'Acesulfame K',
  primaryUse: 'Zero-calorie artificial sweetener common in diet or no-sugar drinks.',
  source: 'Artificial sweetener.',
  category: 'Miscellaneous & New Additives'
}, {
  code: 'E951',
  name: 'Aspartame',
  primaryUse: 'Artificial sweetener used in soft drinks and chewing gum.',
  source: 'Has a mandatory warning label for phenylketonurics.',
  category: 'Miscellaneous & New Additives'
}, {
  code: 'E955',
  name: 'Sucralose',
  primaryUse: 'Non-nutritive sweetener.',
  source: 'Derived from sugar.',
  category: 'Miscellaneous & New Additives'
}, {
  code: 'E960',
  name: 'Steviol Glycosides',
  primaryUse: 'Stevia sweetener.',
  source: 'Derived from stevia.',
  category: 'Miscellaneous & New Additives'
}, {
  code: 'E967',
  name: 'Xylitol',
  primaryUse: 'Sugar alcohol.',
  source: 'Used as a sweetener.',
  category: 'Miscellaneous & New Additives'
}, {
  code: 'E1520',
  name: 'Propylene Glycol',
  primaryUse: 'Humectant that keeps foods moist and carrier for flavourings and colourants.',
  source: 'Food humectant and carrier.',
  category: 'Miscellaneous & New Additives'
}];
export function ENumberDirectory() {
  const [selectedEntry, setSelectedEntry] = useState<ENumberEntry | null>(null);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedEntry(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  return <section className="not-prose rounded-3xl border border-brand-200 bg-brand-50 p-6 sm:p-8" aria-labelledby="e-number-directory-title" data-id="element-491" data-mp="3q3b03kqrvy62">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between" data-id="element-492" data-mp="32cornscuwbvj">
        <div data-id="element-493" data-mp="2xff30exak48r">
          <p className="text-xs font-black uppercase tracking-wider text-brand-700" data-id="element-494" data-mp="2xdu9msmpqrch">Interactive reference</p>
          <h2 id="e-number-directory-title" className="mt-2 text-2xl font-black text-slate-900" data-id="element-495" data-mp="11azax8c18wbu">E-number directory</h2>
          <p className="mt-2 max-w-2xl text-slate-700" data-id="element-496" data-mp="168ha4708zx6n">Select an E number to see its name, primary use, and source.</p>
        </div>
        <p className="text-sm font-medium text-brand-700" data-id="element-497" data-mp="1f2nn1bma7kst">{entries.length} common entries</p>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" aria-label="E-number directory entries" data-id="element-498" data-mp="icwiejka0h9a">
        {entries.map(entry => <button key={entry.code} type="button" onClick={() => setSelectedEntry(entry)} className="rounded-full border border-brand-200 bg-white px-3 py-2 text-sm font-black text-brand-700 transition-colors hover:bg-brand-600 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2" aria-haspopup="dialog" aria-label={`View details for ${entry.code}`} data-id="element-499" data-mp="ncr5ec0xkmp2">
            {entry.code}
          </button>)}
      </div>

      {selectedEntry && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4" role="presentation" onMouseDown={() => setSelectedEntry(null)} data-id="element-500" data-mp="2cw51z1mpf0p8">
          <div role="dialog" aria-modal="true" aria-labelledby="e-number-dialog-title" className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl" onMouseDown={event => event.stopPropagation()} data-id="element-501" data-mp="24434nc7xm0j6">
            <div className="flex items-start justify-between gap-4" data-id="element-502" data-mp="1u4m0iz8bvupb">
              <div data-id="element-503" data-mp="1p7vjo9op8v2u">
                <p className="text-sm font-black uppercase tracking-wider text-brand-700" data-id="element-504" data-mp="1p7capih11h0y">{selectedEntry.category}</p>
                <h3 id="e-number-dialog-title" className="mt-2 text-3xl font-black text-slate-900" data-id="element-505" data-mp="1l7bld2se3lf4">{selectedEntry.code}</h3>
              </div>
              <button type="button" onClick={() => setSelectedEntry(null)} className="rounded-xl p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600" aria-label="Close E-number details" data-id="element-506" data-mp="3fj065llayi7f">
                <XIcon className="h-5 w-5" data-id="element-507" data-mp="3dm1s2v8t3kge" />
              </button>
            </div>
            <div className="mt-6 space-y-5" data-id="element-508" data-mp="3hndaogt9j5f3">
              <div data-id="element-509" data-mp="3hmc0sfnzgoyy"><p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-510" data-mp="3hm26ifvwnvng">Name</p><p className="mt-1 text-lg font-bold text-slate-900" data-id="element-511" data-mp="33voykdj0kgy4">{selectedEntry.name}</p></div>
              <div data-id="element-512" data-mp="38myntd0nhi9h"><p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-513" data-mp="38myl3599n45s">Primary use</p><p className="mt-1 leading-relaxed text-slate-700" data-id="element-514" data-mp="2tyau7cad7im6">{selectedEntry.primaryUse}</p></div>
              <div className="rounded-2xl bg-slate-50 p-4" data-id="element-515" data-mp="2ywceu1nfvpzr"><p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-516" data-mp="2yx5cmd4f755r">Source</p><p className="mt-1 leading-relaxed text-slate-700" data-id="element-517" data-mp="2kyfdkry1iver">{selectedEntry.source}</p></div>
            </div>
          </div>
        </div>}
    </section>;
}